<!--
  ConversationsPage — Phase 4-3 / T16.

  Composes the conversation list under a slim header. The header is
  intentionally minimal: tenant + operator email; a Phase 4-6 enhancement
  swaps the right-side label for an account menu.
-->
<script lang="ts">
  import ConversationList from '../components/ConversationList.svelte';
  import TagFilterBar from '../components/TagFilterBar.svelte';
  import NotificationToggle from '../components/NotificationToggle.svelte';
  import { session } from '../lib/stores/session.svelte.js';

  const userLabel = $derived(
    session.state.sso?.user?.displayName ?? session.state.sso?.user?.email ?? 'You',
  );
  const tenantLabel = $derived(session.state.sso?.tenant?.name ?? session.state.sso?.tenant?.code ?? '');

  // G1.7 — persisted tag filter (mirrors admin's inbox.activeTagFilter
  // localStorage key + JSON-array shape). Tenant scope is widget-wide;
  // SSO swap re-mounts the app so we don't need to clear on tenant change
  // the way admin does.
  const TAG_FILTER_STORAGE_KEY = 'widget.activeTagFilter';
  let selectedTagIds = $state<string[]>(loadStoredTagFilter());

  function loadStoredTagFilter(): string[] {
    if (typeof globalThis.localStorage === 'undefined') return [];
    try {
      const raw = globalThis.localStorage.getItem(TAG_FILTER_STORAGE_KEY);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((x): x is string => typeof x === 'string');
    } catch {
      return [];
    }
  }

  function persistTagFilter(ids: string[]) {
    if (typeof globalThis.localStorage === 'undefined') return;
    try {
      if (ids.length === 0) globalThis.localStorage.removeItem(TAG_FILTER_STORAGE_KEY);
      else globalThis.localStorage.setItem(TAG_FILTER_STORAGE_KEY, JSON.stringify(ids));
    } catch {
      // private-mode Safari etc — in-memory state still drives the UI.
    }
  }

  function onTagFilterChange(next: string[]) {
    selectedTagIds = next;
    persistTagFilter(next);
  }

  // ---- G4.3c — assignee filter (mirrors admin Owner dropdown) -----------
  // 'all' = no filter; 'me' = caller's conversations; 'unassigned' = unowned.
  // Persisted separately from tag filter so toggling one doesn't reset the
  // other. Specific-operator filter is intentionally NOT exposed on the
  // toolbar for v1 (matches admin's scoping rationale).
  // Which queue. Not persisted, unlike the two filters below: "closed" is a
  // place you visit to find one thread, not a state to come back to
  // tomorrow, and an inbox that silently opens on the finished pile is an
  // inbox that looks empty.
  let queue = $state<'open' | 'closed'>('open');

  type AssigneeFilterMode = 'all' | 'me' | 'unassigned';
  const ASSIGNEE_FILTER_STORAGE_KEY = 'widget.assigneeFilter';
  let assigneeFilter = $state<AssigneeFilterMode>(loadStoredAssigneeFilter());

  function loadStoredAssigneeFilter(): AssigneeFilterMode {
    if (typeof globalThis.localStorage === 'undefined') return 'all';
    try {
      const raw = globalThis.localStorage.getItem(ASSIGNEE_FILTER_STORAGE_KEY);
      if (raw === 'me' || raw === 'unassigned') return raw;
      return 'all';
    } catch {
      return 'all';
    }
  }

  function persistAssigneeFilter(value: AssigneeFilterMode) {
    if (typeof globalThis.localStorage === 'undefined') return;
    try {
      if (value === 'all') globalThis.localStorage.removeItem(ASSIGNEE_FILTER_STORAGE_KEY);
      else globalThis.localStorage.setItem(ASSIGNEE_FILTER_STORAGE_KEY, value);
    } catch {
      // ignore
    }
  }

  function onAssigneeFilterChange(event: Event) {
    const raw = (event.currentTarget as HTMLSelectElement).value;
    const next: AssigneeFilterMode = raw === 'me' || raw === 'unassigned' ? raw : 'all';
    if (next === assigneeFilter) return;
    assigneeFilter = next;
    persistAssigneeFilter(next);
  }

  // Only the non-'all' values get forwarded — keeps the URL clean and
  // the query key stable for the default view.
  const assigneeFilterParam = $derived<'me' | 'unassigned' | undefined>(
    assigneeFilter === 'all' ? undefined : assigneeFilter,
  );
</script>

<section class="page">
  <header class="bar">
    <div class="title">Inbox</div>
    <NotificationToggle />
    <div class="who" title={tenantLabel}>{userLabel}</div>
  </header>
  <TagFilterBar {selectedTagIds} onChange={onTagFilterChange} />
  <div class="owner-toolbar" role="region" aria-label="Filter conversations">
    <div class="queue-tabs" role="group" aria-label="Queue">
      <button
        type="button"
        class="queue-tab"
        class:active={queue === 'open'}
        aria-pressed={queue === 'open'}
        onclick={() => (queue = 'open')}
      >
        Open
      </button>
      <button
        type="button"
        class="queue-tab"
        class:active={queue === 'closed'}
        aria-pressed={queue === 'closed'}
        onclick={() => (queue = 'closed')}
      >
        Closed
      </button>
    </div>
    <label class="owner-label">
      <span>Owner</span>
      <select
        class="owner-select"
        value={assigneeFilter}
        onchange={onAssigneeFilterChange}
        aria-label="Filter conversations by assignee"
      >
        <option value="all">All</option>
        <option value="me">Mine</option>
        <option value="unassigned">Unassigned</option>
      </select>
    </label>
  </div>
  <div class="body">
    <ConversationList tagIds={selectedTagIds} assignedTo={assigneeFilterParam} status={queue} />
  </div>
</section>

<style>
  .page {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--widget-bg);
    color: var(--widget-fg);
  }
  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid var(--widget-border);
    flex: 0 0 auto;
  }
  .title {
    /* Display per DESIGN.md hierarchy: 17px / 700 / tight tracking. */
    font-size: 17px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.005em;
  }
  .who {
    /* Mono per the Mono Numerics Rule: operator id-like label tracks the
       Live Console signature even though it's text, not numbers. */
    font-family: var(--widget-font-mono);
    font-size: 11.5px;
    font-weight: 500;
    color: var(--widget-fg-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 60%;
  }
  .body {
    flex: 1 1 auto;
    min-height: 0;
  }

  /* G4.3c — Owner filter strip. Slim toolbar between TagFilterBar and
     the list, matching tag-filter's padding / background so the two read
     as one filter zone. */
  .owner-toolbar {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border-bottom: 1px solid var(--widget-divider, oklch(93% 0.006 250));
    background: var(--widget-bg-sunk, oklch(96% 0.006 250));
    flex: 0 0 auto;
  }
  .queue-tabs {
    display: inline-flex;
    gap: 4px;
  }
  .queue-tab {
    border: 1px solid var(--widget-border, oklch(90% 0.006 250));
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    border-radius: 999px;
    padding: 3px 10px;
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }
  .queue-tab.active {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 14%, transparent);
    border-color: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 34%, transparent);
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
  }

  .owner-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
  }
  .owner-label > span {
    font-weight: 600;
    font-size: 10.5px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .owner-select {
    padding: 2px 6px;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 5px;
    font-family: var(--widget-font);
    font-size: 11.5px;
    font-weight: 600;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
    cursor: pointer;
  }
  .owner-select:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
  }
</style>
